/**
 * Example Scenarios for Advanced Calculator
 */

const neutralAdvancedInputs = {
  partnerGender: 'female',
  partnerAge: '',
  partnerSuper: '',
  partnerRetirementAge: '',
  otherAssets: '',
  otherTaxableIncome: '',
  otherNonTaxableIncome: '',
  yourSalary: '',
  partnerSalary: '',
  yourSuperContributions: '',
  partnerSuperContributions: '',
  yourPartTimeEmploymentIncome: '',
  partnerPartTimeEmploymentIncome: '',
  rentPaidFortnightly: '',
  yourTbcUsed: '',
  partnerTbcUsed: '',
  targetFinalBalance: 0,
  monteCarloTargetIncome: '',
  phasedRetirement: false,
  allowWithdrawalsBeforeRetirementAge: false,
  adminFees: 52,
  adminFeePercentage: 0.1,
  investmentFees: 0.5,
  investmentFeeCap: 350,
  transactionCosts: 0.08,
  insuranceFees: '',
  annuityPurchasePrice: '',
  annuityAnnualPayment: '',
  annuityIsIndexed: false,
  definedBenefitPensionAmount: '',
  definedBenefitIsIndexed: false,
  transferFromSMSF: false,
  residentialPropertyValue: '',
  residentialRentalIncome: '',
  residentialPropertyRentalYield: '',
  residentialPropertyCapitalGrowth: '',
  commercialPropertyValue: '',
  commercialRentalIncome: '',
  commercialPropertyRentalYield: '',
  commercialPropertyCapitalGrowth: '',
  collectiblesValue: '',
  collectiblesCapitalGrowth: '',
  otherAlternativesValue: '',
  otherAlternativesCapitalGrowth: '',
  infrastructurePct: 0,
  infrastructureValue: '',
  infrastructureReturnRate: '',
  assetDetails: [],
  showAdvanced: false,
  showAnnuity: false,
  showAlternativeAssets: false
};

export const exampleScenarios = [
  {
    id: 'age-pension-couple',
    name: 'Couple aged 65 starting retirement',
    category: 'age-pension',
    description: 'A couple starting retirement immediately with Age Pension modelling.',
    inputs: {
      relationshipStatus: 'couple',
      yourGender: 'male',
      partnerGender: 'female',
      yourAge: 65,
      partnerAge: 65,
      yourSuper: 420000,
      partnerSuper: 230000,
      yourRetirementAge: 65,
      partnerRetirementAge: 65,
      periodLength: 30,
      startYear: 1980,
      includePension: true,
      isHomeowner: true,
      otherAssets: 80000,
      withdrawalStrategy: 'fixed',
      sp500: 25,
      ausShares: 35,
      intlShares: 20,
      bonds: 15,
      cash: 5
    }
  },
  {
    id: 'sequence-risk-1969',
    name: '1969 historical stress-test',
    category: 'sequence-risk',
    description: 'A new retiree testing whether the plan survives one of the hardest historical retirement starts.',
    inputs: {
      relationshipStatus: 'single',
      yourGender: 'female',
      yourAge: 67,
      yourSuper: 800000,
      yourRetirementAge: 67,
      periodLength: 30,
      startYear: 1969,
      includePension: true,
      isHomeowner: true,
      otherAssets: 60000,
      withdrawalStrategy: 'fixed',
      sp500: 30,
      ausShares: 35,
      intlShares: 20,
      bonds: 10,
      cash: 5
    }
  },
  {
    id: 'retire-at-60',
    name: 'Retire at 60 bridge to Age Pension',
    category: 'early-retirement',
    description: 'A self-funded bridge from age 60 to Age Pension age.',
    inputs: {
      relationshipStatus: 'single',
      yourGender: 'female',
      yourAge: 60,
      yourSuper: 650000,
      yourRetirementAge: 60,
      periodLength: 32,
      startYear: 1973,
      includePension: true,
      isHomeowner: true,
      otherAssets: 50000,
      withdrawalStrategy: 'fixed',
      sp500: 25,
      ausShares: 30,
      intlShares: 20,
      bonds: 20,
      cash: 5
    }
  },
  {
    id: 'couple-phased-retirement',
    name: 'Couple with different retirement ages',
    category: 'phased-retirement',
    description: 'A household where one partner retires first and the younger partner keeps working.',
    inputs: {
      relationshipStatus: 'couple',
      yourGender: 'male',
      partnerGender: 'female',
      yourAge: 62,
      partnerAge: 58,
      yourSuper: 520000,
      partnerSuper: 310000,
      yourRetirementAge: 62,
      partnerRetirementAge: 65,
      yourSalary: 0,
      partnerSalary: 85000,
      periodLength: 34,
      startYear: 1973,
      includePension: true,
      isHomeowner: true,
      otherAssets: 70000,
      phasedRetirement: true,
      withdrawalStrategy: 'fixed',
      sp500: 25,
      ausShares: 35,
      intlShares: 20,
      bonds: 15,
      cash: 5
    }
  },
  {
    id: 'smsf-trustee',
    name: 'SMSF trustee fee and allocation check',
    category: 'smsf',
    description: 'An SMSF couple comparing fees, asset mix, and retirement drawdowns.',
    inputs: {
      relationshipStatus: 'couple',
      yourGender: 'male',
      partnerGender: 'female',
      yourAge: 67,
      partnerAge: 67,
      yourSuper: 750000,
      partnerSuper: 450000,
      yourRetirementAge: 67,
      partnerRetirementAge: 67,
      periodLength: 30,
      startYear: 1987,
      includePension: true,
      isHomeowner: true,
      adminFees: 2500,
      adminFeePercentage: 0.2,
      investmentFees: 0.35,
      withdrawalStrategy: 'fixed',
      sp500: 20,
      ausShares: 40,
      intlShares: 20,
      bonds: 15,
      cash: 5
    }
  }
];

/**
 * Load a scenario into the calculator state
 * @param {string} scenarioId - The ID of the scenario to load
 * @param {Object} stateSetters - Object containing all state setter functions from AdvancedState
 * @returns {boolean} - True if scenario loaded successfully
 */
export const loadScenario = (scenarioId, stateSetters) => {
  const scenario = getScenario(scenarioId);
  if (!scenario) {
    console.warn('Example scenario not found:', scenarioId);
    return false;
  }

  Object.entries({ ...neutralAdvancedInputs, ...scenario.inputs }).forEach(([key, value]) => {
    const setterName = `set${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    if (typeof stateSetters[setterName] === 'function') {
      stateSetters[setterName](value);
    }
  });

  return true;
};

/** Primary couple + single examples shown in-app (free demo picker). */
export const FEATURED_ADVANCED_SCENARIO_IDS = ['age-pension-couple', 'sequence-risk-1969', 'couple-phased-retirement'];

const SCENARIO_ID_ALIASES = {
  'sequence-risk-1929': 'sequence-risk-1969',
  '1929_crash': 'sequence-risk-1969',
  '1929-crash': 'sequence-risk-1969',
  '1973_crash': 'sequence-risk-1969',
  '1973-crash': 'sequence-risk-1969',
  'single-comprehensive': 'sequence-risk-1969',
  'phased-couple': 'couple-phased-retirement',
  'couple-different-retirement-ages': 'couple-phased-retirement',
};

/**
 * Get scenario by ID
 */
export const getScenario = (scenarioId) => {
  const normalizedScenarioId = SCENARIO_ID_ALIASES[scenarioId] || scenarioId;
  return exampleScenarios.find((scenario) => scenario.id === normalizedScenarioId) || null;
};

/**
 * Get all scenarios
 */
export const getAllScenarios = () => {
  return exampleScenarios;
};

/**
 * Get scenarios by category
 */
export const getScenariosByCategory = (category) => {
  return exampleScenarios.filter((scenario) => scenario.category === category);
};
